import { ColumnOrderState, ColumnPinningState } from "@tanstack/react-table";

import { cyrb53 } from "../../utils/string";

import { decodeColumnPinning } from "./codecs/columnPinningCodec";
import { PresetSearchParamsCustomDefaultValues } from "./types";

// Positional params encode column layout relative to the default order, so they
// only stay valid while that baseline is unchanged.
export const COLUMN_ORDER_PARAM_NAME = "order";
export const COLUMN_VISIBILITY_PARAM_NAME = "visibility";
export const COLUMN_PINNING_PARAM_NAME = "pinning";

// Query param for fingerprint
export const TABLE_BASELINE_PARAM_NAME = "tv";

// Fingerprint of the default column order; `undefined` when there's no baseline.
export function columnBaselineFingerprint(
    defaultOrder: ColumnOrderState | undefined
): string | undefined {
    if (!defaultOrder || defaultOrder.length === 0) {
        return undefined;
    }
    // 4 base-36 chars; a collision only risks one stale layout not resetting.
    return cyrb53(defaultOrder.join(" ")).slice(0, 4);
}

/** True when the URL carries any positional param (order/visibility/pinning). */
export function hasPositionalParam(params: URLSearchParams): boolean {
    return (
        params.has(COLUMN_ORDER_PARAM_NAME) ||
        params.has(COLUMN_VISIBILITY_PARAM_NAME) ||
        params.has(COLUMN_PINNING_PARAM_NAME)
    );
}

/**
 * Returns a copy of `params` with the baseline fingerprint added when a
 * positional param is present, or removed when none is.
 */
export function withBaselineFingerprint(
    params: URLSearchParams,
    baselineFingerprint: string | undefined
): URLSearchParams {
    const next = new URLSearchParams(params);
    if (hasPositionalParam(next) && baselineFingerprint) {
        next.set(TABLE_BASELINE_PARAM_NAME, baselineFingerprint);
    } else {
        next.delete(TABLE_BASELINE_PARAM_NAME);
    }
    return next;
}

// True when positional params exist but their fingerprint doesn't match the baseline.
export function isBaselineStale(
    search: string,
    baselineFingerprint: string | undefined
): boolean {
    if (!baselineFingerprint) {
        return false;
    }
    const params = new URLSearchParams(search);
    if (!hasPositionalParam(params)) {
        return false;
    }
    return params.get(TABLE_BASELINE_PARAM_NAME) !== baselineFingerprint;
}

export function readInitialColumnPinning(
    search: string,
    defaultValues: PresetSearchParamsCustomDefaultValues
): ColumnPinningState | undefined {
    if (!defaultValues.columnOrder) {
        return defaultValues.columnPinning;
    }
    const encoded = new URLSearchParams(search).get(COLUMN_PINNING_PARAM_NAME);
    if (typeof encoded !== "string") {
        return defaultValues.columnPinning;
    }
    const decoded = decodeColumnPinning(
        encoded,
        defaultValues.columnOrder,
        defaultValues.columnPinning
    );
    return decoded ?? defaultValues.columnPinning;
}
